import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import moment from 'moment';
import { MifClientTokenStorageService } from '@shared/auth/mif-client-token-storage.service';
import { ResumeAuthService } from '@shared/auth/resume-auth.service';
import {
  Profile,
  ProfileStatus,
  Subscription
} from 'projects/mif/app/shared/webapi/MIF.Subscription.WebApi';
import { CustomerService } from 'projects/mif/app/shared/webapi/services/customer.service';
import { SettingsService } from 'projects/mif/app/shared/webapi/services/settings.service';
import { Observable } from 'rxjs';

@Injectable()
export class SubscriptionGuard implements CanActivate {
  constructor(
    private settingsService: SettingsService,
    private customerService: CustomerService,
    private router: Router,
    private resumeAuthService: ResumeAuthService,
    private mifClientTokenStorageService: MifClientTokenStorageService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    if (this.resumeAuthService.consumeAuthorizedReturn(state.url)) {
      return true;
    }

    const userId = next.queryParams?.userId;

    if (typeof userId === 'string' && userId.trim() && !this.mifClientTokenStorageService.getToken()) {
      return this.resumeAuthService.redirectToResume(userId, state.url).then(() => false);
    }

    return this.settingsService
      .getSubscriptionConfiguration()
      .then((subscriptionConfiguration: Subscription) => {
        return this.customerService
          .getUserData(userId)
          .then((profile: Profile) => {
            if (next.queryParams?.userId && profile === undefined) {
              // case when userId param is wrong
              return this.redirectToSubscriptionTimeEnd();
            }

            const subscriptionAgeInDays = moment().diff(
              profile?.createdOn,
              'days',
              false
            );
            const profileIsCompleted =
              profile?.profileStatus === ProfileStatus.Completed;

            if (subscriptionAgeInDays === 0 && !profileIsCompleted) {
              return true;
            }

            const expirationPeriod = subscriptionConfiguration.expirationPeriod;
            const subscriptionTimeEnd =
              expirationPeriod !== undefined &&
              subscriptionAgeInDays > expirationPeriod;

            if (subscriptionTimeEnd) {
              return this.customerService.clearAllData().then(() => {
                return this.redirectToSubscriptionTimeEnd();
              });
            } else if (profileIsCompleted) {
              return this.redirectToSubscriptionComplete();
            } else {
              return true;
            }
          })
          .catch(async (err: any) => {
            if (this.resumeAuthService.isUnauthorizedError(err)) {
              await this.customerService.clearAllData();
              await this.resumeAuthService.redirectToResume(userId, state.url);
              return false;
            }

            return this.redirectToSubscriptionTimeEnd();
          });
      });
  }

  private redirectToSubscriptionComplete(): boolean {
    this.router.navigate(['subscription-complete']);
    return false;
  }

  private redirectToSubscriptionTimeEnd(): boolean {
    this.router.navigate(['subscription-time-end']);
    return false;
  }
}
